import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from './AppSidebar';
import { HeaderSlotProvider } from './header-actions';
import { navItemByPath } from '../nav';
import { useGymAdmin } from '../context/gym-admin-context';
import { useSidebarOpen } from '../hooks/useSidebarOpen';

/**
 * Desktop-first shell: a persistent sidebar (collapsible to a rail on desktop,
 * to a Sheet drawer on mobile) plus a header carrying the sidebar trigger, the
 * current section title and whatever the open page puts at its right-hand end
 * (see `HeaderActions`). Page content renders in the <Outlet>.
 *
 * The sidebar is driven as a controlled component so that shrinking it to the
 * rail outlives a reload.
 */
export function GymAdminLayout() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useSidebarOpen();
  const { locationType, sectionPath } = useGymAdmin();
  const activeItem = navItemByPath(sectionPath, locationType);
  /**
   * Where a page's own buttons go. Held in state rather than a ref because the
   * pages portal into it, and a portal has to be told once the element exists.
   */
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
      <AppSidebar />
      {/* `min-w-0`: as a flex child the inset defaults to `min-width: auto`, so a
          wide page (the route-setting board) would stretch the whole shell and
          scroll the window instead of scrolling inside itself.
          `h-svh` pins the shell to the window: the header stays where it is and
          the page below it scrolls, which is what lets the board keep its column
          headers on screen. */}
      <SidebarInset className="h-svh min-w-0 overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-sm font-medium">
            {activeItem ? t(activeItem.labelKey) : t('gymAdmin.title')}
          </h1>
          {/* The open page's own buttons. Controls, so they are off the paper
              like every other handle on a printable page. */}
          <div
            ref={setHeaderSlot}
            className="ml-auto flex items-center gap-2 print:hidden"
            data-testid="header-actions"
          />
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6">
          <HeaderSlotProvider slot={headerSlot}>
            <Outlet />
          </HeaderSlotProvider>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
